import { Button, Heading, LuIcon, Separator, Text } from "@overmap-ai/blocks"
import { type Document, type Stored } from "@overmap-ai/common"
import { selectOrganizationDocuments } from "@overmap-ai/core"
import { getRouteApi, useNavigate } from "@tanstack/react-router"
import { memo, useCallback } from "react"

import { ViewContainer } from "../../components/Containers/ViewContainer"
import { DocumentTree, useDocumentTree } from "../../components/Trees/DocumentTree"
import { modelIconMapping, modelIconNameMapping } from "../../mappings/icons"
import { sdk } from "../../sdk"
import { useShallowEqaulSelector } from "../../store"
import { Colors } from "../../utils/colors"

const routeApi = getRouteApi("/_loggedIn/$organizationId/_map/_layout/documents/")

export const OrganizationDocumentsView = memo(() => {
	const { organizationId } = routeApi.useParams()

	const navigate = useNavigate()
	const organizationDocuments = useShallowEqaulSelector(selectOrganizationDocuments(organizationId))
	const organizationDocumentTree = useDocumentTree(organizationDocuments)

	const handleClickDocument = useCallback(
		(document: Stored<Document>) => {
			void navigate({
				from: "/$organizationId/documents/",
				to: "/$organizationId/documents/$documentId",
				params: (prev) => ({
					...prev,
					documentId: document.uuid,
				}),
			})
		},
		[navigate],
	)

	const handleAddOrganizationDocument = useCallback(
		(parentDocument: Stored<Document> | null) => {
			const [offlineDocument, _promise] = sdk.documents.add({
				title: "",
				description: null,
				content: null,
				parent_document: parentDocument?.uuid ?? null,
				organization: organizationId,
				project: null,
				icon: modelIconNameMapping.document,
				color: Colors.gray,
			})

			void navigate({
				from: "/$organizationId/documents/",
				to: "/$organizationId/documents/$documentId",
				params: (prev) => ({
					...prev,
					documentId: offlineDocument.uuid,
				}),
			})
		},
		[navigate, organizationId],
	)

	const handleAddOrganizationRootDocument = useCallback(() => {
		handleAddOrganizationDocument(null)
	}, [handleAddOrganizationDocument])

	return (
		<ViewContainer maxWidth="6xl">
			<section className="flex size-full flex-col gap-4">
				<div className="flex items-center justify-between gap-2">
					<Heading size="xl">Organization Documents</Heading>

					<Button variant="surface" size="sm" onClick={handleAddOrganizationRootDocument}>
						<LuIcon icon={modelIconMapping.add} />
						Create
					</Button>
				</div>

				<Separator size="full" />

				{organizationDocuments.length > 0 ? (
					<DocumentTree
						tree={{ ...organizationDocumentTree }}
						onClickDocument={handleClickDocument}
						onAddDocument={handleAddOrganizationDocument}
					/>
				) : (
					<Text accentColor="base" size="sm">
						No documents.
					</Text>
				)}
			</section>
		</ViewContainer>
	)
})
OrganizationDocumentsView.displayName = "OrganizationDocumentsView"
